import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const PERIOD_OPTIONS = [
  { label: "1 month", value: "1" },
  { label: "3 months", value: "3" },
  { label: "6 months", value: "6" },
  { label: "12 months", value: "12" },
  { label: "24 months", value: "24" },
  { label: "All time", value: "all" },
];
const DEFAULT_PERIOD = "12";
// Multi-selects fire once per tick; wait for the user to stop clicking.
const SETTLE_MS = 350;

const splitIds = (raw) => (raw ? raw.split(",").filter(Boolean) : []);

/** Filters live in the URL (?cost_center=1,2&department=3&months=12) so a
 *  filtered view can be bookmarked, shared, and carried across the two
 *  statistics pages by their tab links. */
export const useStatsFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      costCenters: splitIds(searchParams.get("cost_center")),
      departments: splitIds(searchParams.get("department")),
      months: searchParams.get("months") || DEFAULT_PERIOD,
    }),
    [searchParams]
  );

  // Rebuilt from the filters, keeping only `tab` from the current URL so a
  // filter change never switches the user to another tab.
  const write = (next) => {
    const params = {};
    const tab = searchParams.get("tab");
    if (tab) params.tab = tab;
    if (next.costCenters.length) params.cost_center = next.costCenters.join(",");
    if (next.departments.length) params.department = next.departments.join(",");
    if (next.months !== DEFAULT_PERIOD) params.months = next.months;
    setSearchParams(params, { replace: true });
  };

  const update = (patch) => write({ ...filters, ...patch });
  const reset = () => write({ costCenters: [], departments: [], months: filters.months });
  const activeCount = (filters.costCenters.length ? 1 : 0) + (filters.departments.length ? 1 : 0);

  return { filters, update, reset, activeCount };
};

const toParams = ({ costCenters, departments, months }, withPeriod) => {
  const params = withPeriod ? { months } : {};
  if (costCenters.length) params.cost_center = costCenters.join(",");
  if (departments.length) params.department = departments.join(",");
  return params;
};

/** One endpoint for the current filters, latest request wins: one fetch per
 *  settled filter change, never twice on mount. `withPeriod` sends the
 *  months window (the turnover page only). */
export const useStatsData = (fetcher, filters, { withPeriod = false } = {}) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const key = JSON.stringify(toParams(filters, withPeriod));

  useEffect(() => {
    let ignore = false;
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    const timer = setTimeout(() => {
      fetcher(JSON.parse(key))
        .then((response) => {
          if (!ignore) setState({ loading: false, error: "", data: response.data });
        })
        .catch((e) => {
          if (ignore) return;
          const forbidden = e?.response?.status === 403;
          setState((prev) => ({
            ...prev,
            loading: false,
            error: forbidden ? "You do not have access to employee statistics." : "Could not load statistics. Try again.",
          }));
        });
    }, SETTLE_MS);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [key, fetcher]);

  return state;
};
